import { TICKET_BULK_MAX_IDS, type TicketDto } from '@planuze/admin-contracts';
import {
  Button,
  ConfirmDialog,
  InlineAlert,
  Page,
  PageContent,
  PageDescription,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  TableSkeleton,
  toast,
} from '@planuze/ui';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  InboxFilters,
  type InboxFiltersValue,
  type PeriodFilter,
} from '../components/support/inbox-filters.js';
import { InboxTable } from '../components/support/inbox-table.js';
import { SupportBulkBar } from '../components/support/support-bulk-bar.js';
import { useSupportInboxContent } from '../content/sections/support-inbox.js';
import { useBulkTicketAction, useTicketsList } from '../hooks/use-tickets.js';

/* Default 'all' (não 'open'): ao atender, o ticket vira `in_progress` e, com o filtro
   fixo em 'open', sumia da tabela ao recarregar (ADR-0433). 'Todos' mantém os atendidos
   visíveis; o staff filtra por status quando quiser. */
const DEFAULT_FILTERS: InboxFiltersValue = {
  search: '',
  status: 'all',
  priority: 'all',
  assignment: 'all',
  period: 'all',
  segment: 'all',
};

/* Janelas do filtro de período em ms (técnico-UI, não business value): convertidas em
   `createdFrom` (ISO) para o filtro server-side de janela de criação (ADR-0352). */
const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const PERIOD_WINDOW_MS: Record<Exclude<PeriodFilter, 'all'>, number> = {
  '24h': 24 * HOUR_MS,
  '7d': 7 * DAY_MS,
  '30d': 30 * DAY_MS,
};

/* Piso da busca por assunto: espelha o `min(2)` do `listTicketsQuery` — 1 char não vai ao
   servidor (evita 400) e não filtra nada localmente. */
const MIN_SEARCH_LEN = 2;

export function SupportInboxPage() {
  const copy = useSupportInboxContent();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<InboxFiltersValue>(DEFAULT_FILTERS);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [pendingBulk, setPendingBulk] = useState<{
    readonly action: 'assign' | 'close';
    readonly ids: readonly string[];
  } | null>(null);
  const bulk = useBulkTicketAction();

  /* Todas as facetas são server-side (ADR-0352): assunto (ILIKE), status/prioridade,
     atribuição (meus/não-atribuídos), origem (org/individual) e período (createdFrom). */
  const search = filters.search.trim();
  /* `createdFrom` é ancorado no momento em que o período é escolhido (memo por `period`):
     recomputar a cada render mudaria o timestamp → queryKey instável → refetch em loop. */
  const createdFrom = useMemo(
    () =>
      filters.period === 'all'
        ? undefined
        : new Date(Date.now() - PERIOD_WINDOW_MS[filters.period]).toISOString(),
    [filters.period],
  );

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useTicketsList({
      status: filters.status === 'all' ? undefined : (filters.status as TicketDto['status']),
      priority:
        filters.priority === 'all' ? undefined : (filters.priority as TicketDto['priority']),
      assignment: filters.assignment,
      search: search.length >= MIN_SEARCH_LEN ? search : undefined,
      createdFrom,
      segment: filters.segment === 'all' ? undefined : filters.segment,
    });

  const items = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data?.pages]);
  /* O lote só alcança tickets ativos que continuam visíveis. Assim, trocar filtros entre a
     seleção e a confirmação nunca envia ids que o operador já não está vendo. */
  const eligibleItems = useMemo(
    () => items.filter((ticket) => ticket.status !== 'closed'),
    [items],
  );
  const selectedItems = useMemo(
    () => eligibleItems.filter((ticket) => selectedIds.has(ticket.id)),
    [eligibleItems, selectedIds],
  );

  const toggle = (id: string): void =>
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else if (next.size < TICKET_BULK_MAX_IDS) next.add(id);
      return next;
    });

  const toggleAll = (checked: boolean): void =>
    setSelectedIds(
      checked
        ? new Set(eligibleItems.slice(0, TICKET_BULK_MAX_IDS).map((ticket) => ticket.id))
        : new Set(),
    );

  const prepareBulk = (action: 'assign' | 'close'): void => {
    const ids = selectedItems.slice(0, TICKET_BULK_MAX_IDS).map((ticket) => ticket.id);
    if (ids.length > 0) setPendingBulk({ action, ids });
  };

  const hasFilters =
    search.length > 0 ||
    filters.status !== 'all' ||
    filters.priority !== 'all' ||
    filters.assignment !== 'all' ||
    filters.period !== 'all' ||
    filters.segment !== 'all';

  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <PageTitle>{copy.pageTitle}</PageTitle>
          <PageDescription>{copy.pageDescription}</PageDescription>
        </PageTitleBlock>
      </PageHeader>
      <PageContent className="space-y-4">
        <InboxFilters value={filters} onChange={setFilters} />
        <SupportBulkBar
          selectedCount={selectedItems.length}
          busy={bulk.isPending}
          onAssign={() => prepareBulk('assign')}
          onClose={() => prepareBulk('close')}
          onClear={() => setSelectedIds(new Set())}
        />
        {isError ? (
          <InlineAlert variant="destructive">{copy.loadError}</InlineAlert>
        ) : isLoading ? (
          <TableSkeleton columns={7} />
        ) : (
          <InboxTable
            items={items}
            hasFilters={hasFilters}
            onClearFilters={() => setFilters(DEFAULT_FILTERS)}
            onRowClick={(id) => {
              navigate({ to: '/support/$ticketId', params: { ticketId: id } }).catch(
                () => undefined,
              );
            }}
            selectedIds={selectedIds}
            onToggle={toggle}
            onToggleAll={toggleAll}
          />
        )}
        {hasNextPage && (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              loading={isFetchingNextPage}
              onClick={() => {
                fetchNextPage().catch(() => undefined);
              }}
            >
              {copy.loadMoreButton}
            </Button>
          </div>
        )}
      </PageContent>
      <ConfirmDialog
        open={pendingBulk !== null}
        onOpenChange={(open) => {
          if (!open) setPendingBulk(null);
        }}
        title={pendingBulk?.action === 'close' ? copy.bulk.closeTitle : copy.bulk.assignTitle}
        description={
          pendingBulk?.action === 'close'
            ? copy.bulk.closeDescription(pendingBulk.ids.length)
            : copy.bulk.assignDescription(pendingBulk?.ids.length ?? 0)
        }
        inputLabel={pendingBulk?.action === 'close' ? copy.bulk.closeReasonLabel : undefined}
        placeholder={pendingBulk?.action === 'close' ? copy.bulk.closeReasonPlaceholder : undefined}
        validate={
          pendingBulk?.action === 'close'
            ? (value) => (value.trim().length >= 10 ? null : copy.bulk.closeReasonError)
            : undefined
        }
        confirmLabel={
          pendingBulk?.action === 'close' ? copy.bulk.closeConfirm : copy.bulk.assignConfirm
        }
        variant={pendingBulk?.action === 'close' ? 'destructive' : 'default'}
        mapError={() => copy.bulk.error}
        onConfirm={async (value) => {
          if (pendingBulk === null || pendingBulk.ids.length === 0) return;
          const { action, ids } = pendingBulk;
          const response = await bulk.mutateAsync(
            action === 'close'
              ? { ids: [...ids], action: 'close', closedReason: value.trim() }
              : { ids: [...ids], action: 'assign' },
          );
          const failed = response.results.filter((result) => !result.ok);
          if (failed.length === 0) toast.success(copy.bulk.success(response.results.length));
          else {
            toast.warning(
              copy.bulk.partial(response.results.length - failed.length, failed.length),
            );
          }
          setSelectedIds(new Set(failed.map((result) => result.id)));
          setPendingBulk(null);
        }}
      />
    </Page>
  );
}
